import api from './api';

/** Tranche B3 (#489): KI-Qualitätsprüfung v2 — async reports per course. */

export type QualityStatus = 'pending' | 'processing' | 'completed' | 'failed';
export type QualityDimensionKey = 'structure' | 'content_depth' | 'engagement' | 'accessibility' | 'assessment_coverage' | 'compliance' | 'readability';

export interface QualitySuggestion {
    category: string;
    severity: 'high' | 'medium' | 'low';
    suggestion: string;
    affected_lessons: string[];
}

export interface QualityStrength {
    category: string;
    description: string;
}

export interface QualityDetails {
    model_overall?: number;
    compliance: { applicable: boolean; checklist: { item: string; label: string; covered: boolean; evidence: string | null }[] };
    readability: { score: number | null; grade: number | null; lessons: { lesson_id: number | null; title: string; grade: number; score: number; words: number }[] };
    /** #516 — learner outcome + lesson funnel the model saw (null in reports from before). */
    learner_data?: LearnerData | null;
}

export interface LearnerDataLesson {
    lesson_id: number | null;
    title: string;
    users_started: number;
    users_completed: number;
    completion_rate: number | null;
    exit_rate: number | null;
    avg_score: number | null;
    avg_time_minutes: number | null;
    drop_off: boolean;
    low_score: boolean;
}

export interface LearnerData {
    sufficient: boolean;
    enrollments: number;
    completed: number;
    in_progress: number;
    failed: number;
    completion_rate: number | null;
    avg_progress: number | null;
    avg_score: number | null;
    avg_time_minutes: number | null;
    lessons: LearnerDataLesson[];
}

export interface QualityReportSummary {
    id: number;
    courseId: number;
    requestedBy: number | null;
    status: QualityStatus;
    overallScore: number | null;
    model: string | null;
    errorMessage: string | null;
    createdAt: string;
    completedAt: string | null;
}

export interface QualityReport extends QualityReportSummary {
    dimensions: Partial<Record<QualityDimensionKey, number | null>> | null;
    suggestions: QualitySuggestion[] | null;
    strengths: QualityStrength[] | null;
    details: QualityDetails | null;
}

export interface QualityCourseInfo {
    id: number;
    title: string;
    contentFormat: 'classic' | 'blocks';
    qualityScore: number | null;
    qualityCheckedAt: string | null;
}

export const DIMENSION_KEYS: QualityDimensionKey[] = ['structure', 'content_depth', 'engagement', 'accessibility', 'assessment_coverage', 'compliance', 'readability'];

export const isReportRunning = (status: QualityStatus): boolean => status === 'pending' || status === 'processing';

export const qualityService = {
    async start(courseId: number): Promise<QualityReport> {
        const { data } = await api.post(`/courses/${courseId}/quality-check`);
        return data;
    },
    async listReports(courseId: number): Promise<{ course: QualityCourseInfo; reports: QualityReportSummary[] }> {
        const { data } = await api.get(`/courses/${courseId}/quality-reports`);
        return data;
    },
    async getReport(reportId: number): Promise<QualityReport> {
        const { data } = await api.get(`/quality-reports/${reportId}`);
        return data;
    },
};

export default qualityService;
